import { ActiveVehicle } from "@lib/data/fitment"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { clx } from "@modules/common/components/ui"

const CarIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 11l1.5-4.5A2 2 0 0 1 8.4 5h7.2a2 2 0 0 1 1.9 1.5L19 11" />
    <path d="M3 13a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4h-2m-14 0H3v-4z" />
    <circle cx="7.5" cy="17" r="1.75" />
    <circle cx="16.5" cy="17" r="1.75" />
    <path d="M9.25 17h5.5" />
  </svg>
)

type GarageButtonProps = {
  vehicle: ActiveVehicle | null
  variant?: "nav" | "side-menu"
}

// Always-visible "My Garage" entry point. Shows the active vehicle compactly
// when one is set, otherwise prompts the shopper to pick one. Links to the
// vehicle selector page.
const GarageButton = ({ vehicle, variant = "nav" }: GarageButtonProps) => {
  const label = vehicle
    ? `${vehicle.year} ${vehicle.make} ${vehicle.model}`
    : "Select Your Vehicle"

  if (variant === "side-menu") {
    return (
      <LocalizedClientLink
        href="/vehicle"
        className="flex items-center gap-x-3 rounded-md border border-white/20 px-3 py-2 hover:border-white/50 transition-colors"
        data-testid="side-menu-garage-link"
      >
        <CarIcon className="shrink-0" />
        <span className="flex flex-col min-w-0 text-left">
          <span className="truncate text-base leading-6">{label}</span>
          {vehicle?.engine && (
            <span className="truncate text-xs opacity-70">
              {vehicle.engine}
            </span>
          )}
        </span>
      </LocalizedClientLink>
    )
  }

  return (
    <LocalizedClientLink
      href="/vehicle"
      className={clx(
        "flex items-center gap-x-2 max-w-[220px] hover:text-white transition-colors",
        vehicle && "text-amber-500 hover:text-amber-400"
      )}
      aria-label={
        vehicle ? `My Garage: ${label}` : "Select your vehicle"
      }
      data-testid="nav-garage-link"
    >
      <CarIcon className="shrink-0" />
      <span className="flex flex-col min-w-0 leading-tight text-left">
        <span className="truncate">{label}</span>
        {vehicle?.engine && (
          <span className="truncate text-[11px] opacity-80">
            {vehicle.engine}
          </span>
        )}
      </span>
    </LocalizedClientLink>
  )
}

export default GarageButton
